import React, { useState, useCallback, useRef, useEffect } from "react";
import { Label } from "../../ui/label";
import { Button } from "../../ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { RichTextEditor } from "../RichTextEditor";
import { InternalControlData } from "../types";
import { DependentControlSelectorDialog } from "./DependentControlSelectorDialog";
import { Trash2 } from "lucide-react";
import { ControlDocumentManager } from "../ControlDocumentManager";

interface PriorYearAuditProps {
  data: InternalControlData;
  onChange: (data: Partial<InternalControlData>) => void;
}

export const PriorYearAudit: React.FC<PriorYearAuditProps> = ({ data, onChange }) => {
  const [showAssociatedControlDialog, setShowAssociatedControlDialog] = useState(false);

  // Use ref to access latest onChange without causing callback recreation
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Stabilize callbacks for ControlDocumentManager to prevent infinite loops
  const handlePriorYearDocumentsChange = useCallback((documents: typeof data.priorYearDocuments) => {
    onChangeRef.current({ priorYearDocuments: documents });
  }, []);

  const handlePriorYearTextResponseChange = useCallback((textResponse: typeof data.priorYearTextResponse) => {
    onChangeRef.current({ priorYearTextResponse: textResponse });
  }, []);

  const handleAssociatedControlsSelected = (selectedControls: typeof data.associatedControls) => {
    onChange({ associatedControls: selectedControls });
    setShowAssociatedControlDialog(false);
  };

  const removeAssociatedControl = (id: string) => {
    onChange({
      associatedControls: data.associatedControls.filter((c) => c.id !== id),
    });
  };

  return (
    <div className="bg-white/5 border border-white/10 rounded-lg">
      <div className="border-b border-white/10 p-6">
        <h2 className="text-xl font-semibold text-white">Prior Year Audit Evidence</h2>
      </div>
      <div className="p-6 space-y-6">
        {/* Main Question */}
        <div className="flex items-center gap-4">
          <Label className="flex-1 text-white">
            Are we planning on using prior year audit evidence to test the operating effectiveness
            of the control?
          </Label>
          <Select
            value={data.usePriorYearEvidence ? "Yes" : "No"}
            onValueChange={(value) => onChange({ usePriorYearEvidence: value === "Yes" })}
          >
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-black/90 border-white/10">
              <SelectItem value="Yes">Yes</SelectItem>
              <SelectItem value="No">No</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {data.usePriorYearEvidence && (
          <div className="space-y-6 border-t border-white/10 pt-6">
            {/* Changes Question */}
            <div className="space-y-4">
              <Label className="text-white">
                Considering the current-year design and implementation testing, have there been any
                changes in the process and/or control that occurred subsequent to previous testing?
              </Label>
              <RichTextEditor
                value={data.changesInProcess}
                onChange={(content) => onChange({ changesInProcess: content })}
                placeholder="Describe any changes..."
              />
            </div>

            {/* Significant Changes */}
            <div className="flex items-center gap-4">
              <Label className="flex-1 text-white">Are the above changes considered significant?</Label>
              <Select
                value={data.changesSignificant ? "Yes" : "No"}
                onValueChange={(value) => onChange({ changesSignificant: value === "Yes" })}
              >
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-black/90 border-white/10">
                  <SelectItem value="Yes">Yes</SelectItem>
                  <SelectItem value="No">No</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* If changes are significant */}
            {data.changesSignificant && (
              <div className="flex items-center gap-4 pl-4 border-l-2 border-primary">
                <Label className="flex-1 text-white">
                  Have you tested the operating effectiveness of at least one control for the
                  current year?
                </Label>
                <Select
                  value={data.testedCurrentYear ? "Yes" : "No"}
                  onValueChange={(value) => onChange({ testedCurrentYear: value === "Yes" })}
                >
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-black/90 border-white/10">
                    <SelectItem value="Yes">Yes</SelectItem>
                    <SelectItem value="No">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {data.testedCurrentYear && (
              <div className="space-y-4 pl-4 border-l-2 border-primary">
                <div className="flex items-center gap-4">
                  <Label className="text-white">Associate control from control library:</Label>
                  <Button
                    variant="outline"
                    onClick={() => setShowAssociatedControlDialog(true)}
                    className="border-white/10 text-white hover:bg-white/10"
                  >
                    + Add
                  </Button>
                </div>

                {data.associatedControls.length > 0 && (
                  <div className="border border-white/10 rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-black/40">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-white/60">
                            Control ID
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-white/60">
                            Control Name
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-white/60">
                            Type of Control
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-white/60">
                            Nature
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-white/60">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.associatedControls.map((control) => (
                          <tr key={control.id} className="border-t border-white/10">
                            <td className="px-4 py-3 text-white">
                              {control.controlId || "-"}
                            </td>
                            <td className="px-4 py-3 text-white">
                              {control.controlName || "-"}
                            </td>
                            <td className="px-4 py-3 text-white">
                              {control.controlDescription || "-"}
                            </td>
                            <td className="px-4 py-3 text-white">
                              {control.designImplementation || "-"}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeAssociatedControl(control.id)}
                                className="text-red-400 hover:text-red-300"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Associated Control Selector Dialog */}
            <DependentControlSelectorDialog
              isOpen={showAssociatedControlDialog}
              onClose={() => setShowAssociatedControlDialog(false)}
              onSelect={handleAssociatedControlsSelected}
              existingControls={data.associatedControls}
            />

            {/* Documentation Description */}
            <div className="space-y-4 border-t border-white/10 pt-6">
              <div className="p-4 border border-white/10 rounded-lg bg-black/40">
                <p className="text-sm text-white/60">
                  For each control for which we are using prior year audit evidence, include a
                  sufficient description of the testing performed (may include copies of previous
                  year working papers) to enable an experienced auditor, having no previous
                  connection with the engagement, to understand the nature, timing, extent and
                  results of the procedures performed, evidence obtained and conclusions reached.
                </p>
              </div>
              <ControlDocumentManager
                controlId={data.controlId}
                workspaceLinked={data.workspaceLinked}
                documents={data.priorYearDocuments || []}
                onDocumentsChange={handlePriorYearDocumentsChange}
                textResponse={data.priorYearTextResponse}
                onTextResponseChange={handlePriorYearTextResponseChange}
                label="Prior Year Evidence"
              />
            </div>
          </div>
        )}

        {!data.usePriorYearEvidence && (
          <div className="p-4 border border-white/10 rounded-lg bg-black/40">
            <p className="text-sm text-white/60 italic">
              No prior year audit evidence will be used. Please complete the Risk Associated with 
              Control and Operating Effectiveness sections.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

